import type { SelectPopoverProps } from './SelectPopover';

import { Box, BlockStack, Divider, InlineStack, Button, Scrollable } from '@shopify/polaris';
import { useControlDataChange } from '~/presentation/reports/common';
import { PopoverStatus } from './PopoverStatus';
import { useTranslation } from 'react-i18next';
import { GCheckbox } from '~/presentation/ui';
import { useState, useMemo } from 'react';

export const PopoverChoice = ({
  options,
  defaultValue,
  id,
  isLoading,
  enableCreateOption,
  togglePopoverActive,
}: SelectPopoverProps) => {
  const { t } = useTranslation();
  const [tempSelectedItems, setTempSelectedItems] = useState<string[]>(
    (defaultValue as string[]) || [],
  );
  const { emitControlChange } = useControlDataChange();

  const items = useMemo(() => {
    if (!options?.length) return [];
    const tempDefaultValues = new Set(defaultValue);

    const result = options.map((option) => {
      if (tempDefaultValues.has(option.value)) {
        tempDefaultValues.delete(option.value);
      }
      return {
        ...option,
        selected: false,
      };
    });

    if (enableCreateOption) {
      tempDefaultValues.forEach((value) => {
        result.push({
          label: value,
          value,
          selected: false,
        });
      });
    }

    return result;
  }, [options, defaultValue, enableCreateOption]);

  const handleApplySelection = () => {
    emitControlChange({ [id]: tempSelectedItems });
    togglePopoverActive(false);
  };

  const handleClearSelection = () => {
    setTempSelectedItems([]);
  };

  return (
    <Box paddingBlock={items.length ? '150' : '0'} paddingInline="100">
      <BlockStack>
        <Scrollable scrollbarWidth="thin" style={{ maxHeight: '300px' }}>
          <BlockStack gap="0">
            <GCheckbox
              multiple
              options={items}
              selected={tempSelectedItems}
              onChange={setTempSelectedItems}
            />
          </BlockStack>
        </Scrollable>
        <PopoverStatus isLoading={isLoading} isShowNoData={options?.length === 0} />
        <Box position="sticky" insetBlockEnd="0" background="bg-fill">
          <Divider borderColor="border" />
          <Box paddingBlock="200" paddingInline="300">
            <InlineStack gap="300" align="end">
              <Button
                variant="plain"
                onClick={handleClearSelection}
                disabled={!tempSelectedItems.length || isLoading}
              >
                {t('Clear')}
              </Button>
              <Button
                variant="primary"
                onClick={handleApplySelection}
                disabled={!tempSelectedItems.length || isLoading}
              >
                {t('Apply')}
              </Button>
            </InlineStack>
          </Box>
        </Box>
      </BlockStack>
    </Box>
  );
};
